import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import { TaskFlowDto } from '@shared/services/apiService/apiService';
import {
  TaskEditorValue,
  TaskFieldErrors,
  TaskFlowErrors,
  TaskGroupEditorValue,
} from '../domain/contracts';
import { createEmptyTask, createTaskFlowId, createTaskId } from '../domain/taskTypeRegistry';
import { hasTaskFlowErrors, validateTaskFlow } from '../domain/validation';
import {
  normalizeTaskGroup,
  reorderTasks,
  sanitizeTaskForType,
  serializeTaskGroup,
} from '../utils/task-flow-mapper';
import { TaskFlowTaskType } from '../domain/contracts';

export type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

export const useTaskFlowEditorState = (initialTaskFlow: TaskFlowDto | null | undefined) => {
  const [group, setGroup] = useState<TaskGroupEditorValue | null>(() =>
    normalizeTaskGroup(initialTaskFlow),
  );
  const [selectedTaskKey, setSelectedTaskKey] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [backendErrors, setBackendErrors] = useState<TaskFlowErrors>({
    groupErrors: {},
    taskErrors: {},
  });

  const lastSavedTaskFlowRef = useRef<TaskFlowDto | null>(initialTaskFlow ?? null);
  const initialTaskFlowRef = useRef<TaskFlowDto | null>(initialTaskFlow ?? null);

  useEffect(() => {
    const nextTaskFlow = initialTaskFlow ?? null;
    if (isEqual(nextTaskFlow, initialTaskFlowRef.current)) return;
    initialTaskFlowRef.current = nextTaskFlow;
    lastSavedTaskFlowRef.current = nextTaskFlow;
    setGroup(normalizeTaskGroup(nextTaskFlow));
    setSelectedTaskKey(null);
    setSaveState('idle');
    setBackendErrors({ groupErrors: {}, taskErrors: {} });
  }, [initialTaskFlow]);

  const serializedTaskFlow = useMemo(() => (group ? serializeTaskGroup(group) : null), [group]);
  const isDirty = !isEqual(serializedTaskFlow, lastSavedTaskFlowRef.current);

  const clientErrors = useMemo(() => validateTaskFlow(group), [group]);
  const errors: TaskFlowErrors = useMemo(
    () => ({
      groupErrors: { ...clientErrors.groupErrors, ...backendErrors.groupErrors },
      taskErrors: mergeTaskErrors(clientErrors.taskErrors, backendErrors.taskErrors),
    }),
    [clientErrors, backendErrors],
  );
  const hasErrors = hasTaskFlowErrors(errors);

  const mutateTasks = useCallback((updater: (tasks: TaskEditorValue[]) => TaskEditorValue[]) => {
    setGroup((prev) => (prev ? { ...prev, tasks: updater(prev.tasks) } : prev));
  }, []);

  const startNewGroup = useCallback(() => {
    setGroup({
      id: createTaskFlowId(),
      autoStart: false,
      summarizeChatCtx: true,
      returnExceptions: false,
      tasks: [],
    });
    setSaveState('dirty');
    setBackendErrors({ groupErrors: {}, taskErrors: {} });
  }, []);

  const addTask = useCallback(
    (type: TaskFlowTaskType, description?: string) => {
      const existingTasks = group?.tasks ?? [];
      const task = createEmptyTask(
        type,
        description,
        createTaskId(
          type,
          existingTasks.length + 1,
          new Set(existingTasks.map((existingTask) => existingTask.id)),
        ),
      );
      mutateTasks((tasks) => [...tasks, task]);
      setSelectedTaskKey(task.clientKey);
      setBackendErrors({ groupErrors: {}, taskErrors: {} });
    },
    [group?.tasks, mutateTasks],
  );

  const removeTask = useCallback(
    (clientKey: string) => {
      mutateTasks((tasks) => tasks.filter((task) => task.clientKey !== clientKey));
      setSelectedTaskKey((prev) => (prev === clientKey ? null : prev));
    },
    [mutateTasks],
  );

  const updateTask = useCallback(
    (clientKey: string, updater: (task: TaskEditorValue) => TaskEditorValue) => {
      mutateTasks((tasks) =>
        tasks.map((task) =>
          task.clientKey === clientKey ? sanitizeTaskForType(updater(task)) : task,
        ),
      );
    },
    [mutateTasks],
  );

  const reorderTask = useCallback(
    (fromIndex: number, toIndex: number) => {
      mutateTasks((tasks) => reorderTasks(tasks, fromIndex, toIndex));
    },
    [mutateTasks],
  );

  const updateGroupField = useCallback(
    <K extends keyof TaskGroupEditorValue>(field: K, value: TaskGroupEditorValue[K]) => {
      setGroup((prev) => (prev ? { ...prev, [field]: value } : prev));
    },
    [],
  );

  const markSaved = useCallback(
    (saved: TaskFlowDto | null) => {
      const selectedId = group?.tasks.find((task) => task.clientKey === selectedTaskKey)?.id;
      const nextGroup = normalizeTaskGroup(saved);
      setGroup(nextGroup);
      setSelectedTaskKey(
        selectedId
          ? (nextGroup?.tasks.find((task) => task.id === selectedId)?.clientKey ?? null)
          : null,
      );
      lastSavedTaskFlowRef.current = saved;
      initialTaskFlowRef.current = saved;
      setSaveState('saved');
      setBackendErrors({ groupErrors: {}, taskErrors: {} });
    },
    [group, selectedTaskKey],
  );

  const markSaveError = useCallback((fieldErrors: TaskFlowErrors) => {
    setSaveState('error');
    setBackendErrors(fieldErrors);
  }, []);

  const serializeForSave = useCallback(
    (): TaskFlowDto | null => (group ? serializeTaskGroup(group) : null),
    [group],
  );

  const selectedTask = useMemo(
    () => group?.tasks.find((task) => task.clientKey === selectedTaskKey) ?? null,
    [group, selectedTaskKey],
  );

  return {
    group,
    isDirty,
    saveState,
    setSaveState,
    errors,
    hasErrors,
    selectedTaskKey,
    selectedTask,
    selectTask: setSelectedTaskKey,
    startNewGroup,
    addTask,
    removeTask,
    updateTask,
    reorderTask,
    updateGroupField,
    markSaved,
    markSaveError,
    serializeForSave,
  };
};

function mergeTaskErrors(
  client: Record<string, TaskFieldErrors>,
  backend: Record<string, TaskFieldErrors>,
): Record<string, TaskFieldErrors> {
  const merged: Record<string, TaskFieldErrors> = { ...client };
  for (const [key, value] of Object.entries(backend)) {
    merged[key] = { ...merged[key], ...value };
  }
  return merged;
}
